import { X } from 'lucide-react'
import { useState, useEffect } from 'react'

import { DateQuickSelect } from '@/components/ui/date-quick-select'
import {
  useTimelog,
  useCreateTimelog,
  useUpdateTimelog,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface TimelogFormProps {
  timelogId?: string | null
  issueId?: string
  onClose: () => void
}

/**
 * 工时记录表单组件
 */
export function TimelogForm({ timelogId, issueId, onClose }: TimelogFormProps) {
  const isEditing = !!timelogId
  const { data: timelog, isLoading: isLoadingTimelog } = useTimelog(timelogId || '')
  const createMutation = useCreateTimelog()
  const updateMutation = useUpdateTimelog()

  const [formData, setFormData] = useState({
    issueId: issueId || '',
    logDate: new Date().toISOString().split('T')[0],
    hours: 0,
    minutes: 0,
    description: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 加载编辑数据
  useEffect(() => {
    if (timelog) {
      const totalMinutes = timelog.minutes
      setFormData({
        issueId: timelog.issueId,
        logDate: timelog.logDate,
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
        description: timelog.description || '',
      })
    }
  }, [timelog])

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.issueId) {
      newErrors.issueId = '请选择任务'
    }

    if (!formData.logDate) {
      newErrors.logDate = '请选择日期'
    } else {
      const logDate = new Date(formData.logDate)
      const today = new Date()
      today.setHours(23, 59, 59, 999)
      if (logDate > today) {
        newErrors.logDate = '日期不能是未来'
      }
    }

    const totalMinutes = formData.hours * 60 + formData.minutes
    if (totalMinutes <= 0) {
      newErrors.time = '工时必须大于0'
    } else if (totalMinutes > 1440) {
      newErrors.time = '工时不能超过24小时'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const totalMinutes = formData.hours * 60 + formData.minutes
    const data = {
      issueId: formData.issueId,
      logDate: formData.logDate,
      minutes: totalMinutes,
      description: formData.description || undefined,
    }

    if (isEditing && timelogId) {
      updateMutation.mutate(
        { id: timelogId, data },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          onClose()
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  if (isEditing && isLoadingTimelog) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="rounded-lg bg-background p-6">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background shadow-lg">
        {/* 头部 */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? '编辑工时' : '记录工时'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* 任务选择 */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                任务 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.issueId}
                onChange={(e) => setFormData((prev) => ({ ...prev, issueId: e.target.value }))}
                placeholder="输入任务ID"
                disabled={isEditing}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm',
                  errors.issueId && 'border-destructive',
                  isEditing && 'bg-muted'
                )}
              />
              {errors.issueId && (
                <p className="mt-1 text-sm text-destructive">{errors.issueId}</p>
              )}
            </div>

            {/* 日期 */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                日期 <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                value={formData.logDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, logDate: e.target.value }))}
                max={new Date().toISOString().split('T')[0]}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm',
                  errors.logDate && 'border-destructive'
                )}
              />
              {/* 日期快捷选项 (Requirements: 11.6) - 工时记录只能选择今天或之前的日期 */}
              <DateQuickSelect
                value={formData.logDate}
                onChange={(date) => setFormData((prev) => ({ ...prev, logDate: date }))}
                className="mt-2"
                options={{
                  includeToday: true,
                  includeTomorrow: false,
                  includeNextMonday: false,
                }}
              />
              {errors.logDate && (
                <p className="mt-1 text-sm text-destructive">{errors.logDate}</p>
              )}
            </div>

            {/* 工时 */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                工时 <span className="text-destructive">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={formData.hours}
                  onChange={(e) => setFormData((prev) => ({ ...prev, hours: parseInt(e.target.value) || 0 }))}
                  className={cn(
                    'w-20 rounded-md border bg-background px-3 py-2 text-sm',
                    errors.time && 'border-destructive'
                  )}
                />
                <span className="text-sm text-muted-foreground">小时</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.minutes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, minutes: parseInt(e.target.value) || 0 }))}
                  className={cn(
                    'w-20 rounded-md border bg-background px-3 py-2 text-sm',
                    errors.time && 'border-destructive'
                  )}
                />
                <span className="text-sm text-muted-foreground">分钟</span>
              </div>
              {errors.time && (
                <p className="mt-1 text-sm text-destructive">{errors.time}</p>
              )}
            </div>

            {/* 描述 */}
            <div>
              <label className="mb-2 block text-sm font-medium">描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="工作内容描述..."
                rows={3}
                maxLength={500}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {formData.description.length}/500
              </p>
            </div>
          </div>

          {/* 错误提示 */}
          {(createMutation.error || updateMutation.error) && (
            <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(createMutation.error || updateMutation.error)?.message || '操作失败'}
            </div>
          )}

          {/* 按钮 */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : isEditing ? '保存' : '记录'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
